import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  UserPlus,
  Megaphone,
  TrendingUp,
  ClipboardList,
  Layers,
  Search,
  Bot,
  Target,
  Activity,
  Briefcase,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { PipelineTemperatureWidget } from '@/components/dashboard/PipelineTemperatureWidget';
import { ForecastSummaryWidget } from '@/components/dashboard/ForecastSummaryWidget';
import { RevenueEvolutionWidget } from '@/components/dashboard/RevenueEvolutionWidget';
import { FunnelConversionWidget } from '@/components/dashboard/FunnelConversionWidget';
import { PipelineVelocityWidget } from '@/components/dashboard/PipelineVelocityWidget';
import { AlertsFeedWidget } from '@/components/dashboard/AlertsFeedWidget';
import { PredictabilityWidget } from '@/components/dashboard/PredictabilityWidget';
import { CustomerFlowWidget } from '@/components/dashboard/CustomerFlowWidget';
import { GrowthQualityWidget } from '@/components/dashboard/GrowthQualityWidget';
import { VerticalTileGrid } from '@/components/VerticalTileGrid';
import { SubtreeRollupSection } from '@/components/dashboard/SubtreeRollupSection';
import { ShieldAlert, BarChart3, SearchCheck, Bot as BotIcon } from 'lucide-react';

/* ================================================================== */
/*  Shared types                                                       */
/* ================================================================== */

interface DashboardTile {
  key: string;
  label: string;
  path: string;
  icon: LucideIcon;
}

const ALL_TILES: DashboardTile[] = [
  { key: 'accounts', label: 'Accounts', path: '/accounts', icon: Building2 },
  { key: 'leads', label: 'Leads', path: '/leads', icon: UserPlus },
  { key: 'campaigns', label: 'Campaigns', path: '/campaigns', icon: Megaphone },
  { key: 'opportunities', label: 'Opportunities', path: '/opportunities', icon: TrendingUp },
  { key: 'tasks', label: 'Tasks', path: '/tasks', icon: ClipboardList },
  { key: 'templates', label: 'Templates', path: '/process-templates', icon: Layers },
  { key: 'search', label: 'Search', path: '/search', icon: Search },
  { key: 'agents', label: 'Agents', path: '/admin/agents', icon: Bot },
];

/* ================================================================== */
/*  DashboardData — all fields optional (backend nullable sections)    */
/* ================================================================== */

interface OrgForecastVsTarget {
  forecastTotal: number;
  targetTotal: number;
}

interface PipelineTrendWeek {
  weekEnding: string;
  totalValue: number;
}

interface WinRateSummary {
  winRate: number;
  wonCount: number;
  totalClosed: number;
}

interface AgentSuccessRateSummary {
  successRatePercent: number;
  successCount: number;
  totalRuns: number;
}

interface RevenueCompositionRow {
  revenueType: string;
  totalValue: number;
  count?: number;
}

interface TopDeal {
  opportunityId: string;
  name: string;
  estimatedValue: number;
  stageName?: string;
  assignedRepEmail?: string;
  assignedRepId?: string;
}

// Same shape on the backend for both team / rep attainment views.
type TeamAttainmentRow = RepAttainmentRow;

interface UserAdoption {
  activeUsers: number;
  inactiveUsers: number;
  totalUsers: number;
}

interface TeamPipelineStage {
  stageId?: string;
  stageName?: string;
  count: number;
  totalValue?: number;
}

interface ConversionFunnelData {
  leadToOpportunity: number;
  opportunityToWon: number;
}

interface RiskAlert {
  opportunityId: string;
  opportunityName: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  reasoning: string;
}

interface AreaMetric {
  label: string;
  count: number;
  value: number;
}

interface ActivityAreasData {
  demandGeneration: AreaMetric;
  maturation: AreaMetric;
  close: AreaMetric;
  delivery: AreaMetric;
}

interface OutOfIcpAlert {
  opportunityId: string;
  opportunityName: string;
  estimatedValue: number;
  accountName: string | null;
}

interface IcpPipelineQuality {
  icpConfigured: boolean;
  icpSharePercent: number | null;
  icpPipelineValue: number;
  totalPipelineValue: number;
  outOfIcpAlerts: OutOfIcpAlert[];
  avgAiCloseProb: number | null;
}

interface RevenueClosed {
  total: number;
  byType?: { type: string; total: number }[];
}

interface PipelineWeightedData {
  rawPipeline: number;
  weightedValue?: number;
}

interface MarginSummary {
  totalRevenue?: number;
  totalCost?: number;
  totalMargin?: number;
  averageMarginPercent?: number;
}

interface ForecastVsActual {
  forecast?: number;
  actual?: number;
  variance?: number;
  variancePercent?: number;
}

interface RecurringVsOneTime {
  recurringRevenue?: number;
  oneTimeRevenue?: number;
}

interface CommissionAccruals {
  totalAccrued?: number;
  repCount?: number;
  periodStart?: string;
  periodEnd?: string;
  byRep?: { rep: string; accrued: number; paid: number; balance: number }[];
}

interface QuotaAttainment {
  quotaId: string;
  userId: string;
  targetAmount: number;
  currency: string;
  wonAmount: number;
  attainmentPercent: number;
  periodStart: string;
  periodEnd: string;
}

interface ForecastBucket {
  classification: string;
  totalValue: number;
  count: number | null;
}

interface AgentLogEntry {
  agentId: string;
  status: string;
  confidence: number | null;
  reasoning: string | null;
}

interface RepAttainmentRow {
  userId?: string;
  email: string | null;
  targetAmount: number;
  wonAmount: number;
  attainmentPercent: number;
}

interface DashboardData {
  // Admin / Executive
  orgForecastVsTarget?: OrgForecastVsTarget | null;
  pipelineTrend?: PipelineTrendWeek[] | null;
  winRate?: WinRateSummary | null;
  revenueComposition?: RevenueCompositionRow[] | null;
  topDeals?: TopDeal[] | null;
  teamAttainment?: TeamAttainmentRow[] | null;
  userAdoption?: UserAdoption | null;
  dataQualityIssueCount?: number | null;
  agentSuccessRate?: AgentSuccessRateSummary | null;

  // Manager
  teamPipelineByStage?: TeamPipelineStage[] | null;
  staleCount?: number | null;
  pendingApprovals?: number | null;
  repAttainment?: RepAttainmentRow[] | null;
  conversionFunnel?: ConversionFunnelData | null;
  riskAlerts?: RiskAlert[] | null;
  activityAreas?: ActivityAreasData | null;
  icpPipelineQuality?: IcpPipelineQuality | null;

  // Finance
  revenueClosed?: RevenueClosed | null;
  pipelineWeighted?: PipelineWeightedData | null;
  marginSummary?: MarginSummary | null;
  forecastVsActual?: ForecastVsActual | null;
  recurringVsOneTime?: RecurringVsOneTime | null;
  commissionAccruals?: CommissionAccruals | null;

  // Rep
  quotaAttainment?: QuotaAttainment | null;
  noQuota?: boolean | null;
  forecastBuckets?: ForecastBucket[] | null;
  openOpportunityCount?: number | null;
  agentLog?: AgentLogEntry[] | null;

  // Ticket 114.7 — Demo sections per persona
  demosAwaitingSend?: DemoCockpitRow[] | null;
  recentDemoCtaClicks?: number | null;
  demosAwaitingRefinement?: DemoCockpitRow[] | null;
  demoConversionByRep?: DemoConversionByRep[] | null;
  demoRepUsageZScores?: DemoRepUsageZScore[] | null;
  demosByStatus?: DemoStatusCount[] | null;
  customIndustriesThisQuarter?: number | null;
}

// 114.7 demo types
interface DemoCockpitRow {
  id: string;
  slug: string;
  businessName: string;
  industryKey: string;
  updatedAt: string;
}

interface DemoConversionByRep {
  repId: string;
  repEmail: string | null;
  demosSent: number;
  oppAdvanced: number;
  conversionRate: number;
}

interface DemoRepUsageZScore {
  repId: string;
  repEmail: string | null;
  demosSent: number;
  zScore: number;
  usageFlag: 'UNDER' | 'NORMAL' | 'OVER';
}

interface DemoStatusCount {
  status: string;
  count: number;
}

/* ================================================================== */
/*  LocalStorage helpers (pins + visits)                               */
/* ================================================================== */

const PINNED_KEY = 'kairos.dashboard-pins';
const VISITS_KEY = 'kairos.page-visits';

function loadPins(): string[] {
  try {
    const stored = localStorage.getItem(PINNED_KEY);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
}

function savePins(pins: string[]) {
  localStorage.setItem(PINNED_KEY, JSON.stringify(pins));
}

function recordVisit(path: string) {
  try {
    const stored = localStorage.getItem(VISITS_KEY);
    const visits: Record<string, number> = stored ? JSON.parse(stored) : {};
    visits[path] = (visits[path] || 0) + 1;
    localStorage.setItem(VISITS_KEY, JSON.stringify(visits));
  } catch {
    /* */
  }
}

function getTopVisited(count: number): string[] {
  try {
    const stored = localStorage.getItem(VISITS_KEY);
    if (!stored) return [];
    const visits: Record<string, number> = JSON.parse(stored);
    return Object.entries(visits)
      .sort((a, b) => b[1] - a[1])
      .slice(0, count)
      .map(([path]) => path);
  } catch {
    return [];
  }
}

/* ================================================================== */
/*  Formatting helpers                                                 */
/* ================================================================== */

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value.toFixed(0)}`;
  }
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function attainmentColor(pct: number): string {
  if (pct >= 80) return 'text-green-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-red-600';
}

function attainmentBarColor(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

/* ================================================================== */
/*  Shared sub-components                                              */
/* ================================================================== */

function InfoCard({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  );
}

/* ------------------------------------------------------------------ */
/*  Quick Access Tiles (collapsible or always-open)                    */
/* ------------------------------------------------------------------ */

function QuickAccessTiles({
  collapsible,
  counts,
}: {
  collapsible: boolean;
  counts?: Record<string, number>;
}) {
  const [pins, setPins] = useState<string[]>(loadPins);
  const [editing, setEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(collapsible);
  const topVisited = getTopVisited(5);

  function togglePin(key: string) {
    const next = pins.includes(key) ? pins.filter((p) => p !== key) : [...pins, key];
    setPins(next);
    savePins(next);
  }

  const pinnedTiles =
    pins.length > 0
      ? ALL_TILES.filter((tile) => pins.includes(tile.key))
      : ALL_TILES.filter((tile) => topVisited.includes(tile.path)).slice(0, 5);

  const displayTiles = pinnedTiles.length > 0 ? pinnedTiles : ALL_TILES.slice(0, 4);
  const suggestedLabel = pins.length > 0 ? 'Pinned' : 'Suggested';

  return (
    <div>
      <div className="flex items-center gap-2">
        {collapsible && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {suggestedLabel} Quick Access
          </button>
        )}
        {!collapsible && <SectionHeading>{suggestedLabel} Quick Access</SectionHeading>}
        {!collapsed && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(!editing)}
            className="ml-auto"
          >
            {editing ? 'Done' : 'Customize'}
          </Button>
        )}
      </div>

      {!collapsed && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {displayTiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <Link
                  key={tile.key}
                  to={tile.path}
                  onClick={() => recordVisit(tile.path)}
                  className="flex flex-col items-center gap-2 rounded-xl border bg-card p-5 transition-colors hover:bg-accent"
                >
                  <Icon className="h-8 w-8 text-primary" />
                  <span className="text-sm font-medium">{tile.label}</span>
                  {counts?.[tile.key] !== undefined && (
                    <span className="text-xs text-muted-foreground">
                      {counts[tile.key]} open
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          {editing && (
            <div className="mt-3 rounded-lg border bg-card p-4">
              <h3 className="mb-3 text-sm font-semibold">Pin modules to your dashboard</h3>
              <div className="flex flex-wrap gap-2">
                {ALL_TILES.map((tile) => (
                  <button
                    key={tile.key}
                    onClick={() => togglePin(tile.key)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      pins.includes(tile.key)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {tile.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Agent Insights Summary (shared by rep + manager)                   */
/* ------------------------------------------------------------------ */

const INSIGHT_AGENTS: Record<string, { label: string; icon: typeof ShieldAlert; color: string }> = {
  A5_RISK_SCORING: { label: 'Deals at risk', icon: ShieldAlert, color: 'text-red-600' },
  A2_LEAD_SCORING: { label: 'Leads scored', icon: BarChart3, color: 'text-blue-600' },
  A4_GAP_DETECTION: { label: 'Gaps detected', icon: SearchCheck, color: 'text-amber-600' },
  GATEKEEPER: { label: 'Gate checks', icon: BotIcon, color: 'text-purple-600' },
};

function AgentInsightsSummary({ entries }: { entries: AgentLogEntry[] }) {
  const tiles = Object.entries(INSIGHT_AGENTS)
    .map(([agentId, meta]) => {
      const agentEntries = entries.filter((e) => e.agentId === agentId && e.status === 'SUCCESS');
      if (agentEntries.length === 0) return null;
      const Icon = meta.icon;
      return (
        <Link
          key={agentId}
          to="/admin/agents"
          className="flex items-center gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-accent"
        >
          <Icon className={`h-6 w-6 ${meta.color}`} />
          <div>
            <div className="text-lg font-bold">{agentEntries.length}</div>
            <div className="text-xs text-muted-foreground">{meta.label}</div>
          </div>
        </Link>
      );
    })
    .filter(Boolean);

  if (tiles.length === 0) return null;

  return (
    <div>
      <SectionHeading>AI Agent Activity</SectionHeading>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{tiles}</div>
    </div>
  );
}

/* ================================================================== */
/*  Story 41.6 — DashboardPage (role router)                           */
/* ================================================================== */

export function DashboardPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    recordVisit('/dashboard');
    apiClient
      .get<DashboardData>('/dashboard-data')
      .then((r) => setData(r.data))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const safeData = data ?? {};

  switch (role) {
    case 'SALES_MANAGER':
      return <ManagerDashboard data={safeData} />;
    case 'FINANCE':
      return <FinanceDashboard data={safeData} />;
    case 'TENANT_ADMIN':
    case 'PLATFORM_ADMIN':
      return <AdminDashboard data={safeData} role={role} />;
    default:
      return <RepDashboard data={safeData} />;
  }
}

/* ================================================================== */
/*  Story 41.2 — Admin / Executive Dashboard                           */
/* ================================================================== */

function AdminDashboard({ data, role }: { data: DashboardData; role: string }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>

      {/* Stories 56.1 + 56.5 + 57.1 — owner-facing hero strip */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PipelineTemperatureWidget />
        <ForecastSummaryWidget />
      </div>
      <RevenueEvolutionWidget />
      {/* Stories 56.2 + 56.3 + 57.6 — funnel + velocity + predictability */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FunnelConversionWidget />
        <PipelineVelocityWidget />
      </div>
      <PredictabilityWidget />
      {/* Stories 57.3 + 57.4 — customer flow + growth quality */}
      <CustomerFlowWidget />
      <GrowthQualityWidget />
      <AlertsFeedWidget />

      {/* Row 1 — Org Forecast vs Target (hero) */}
      {data.orgForecastVsTarget && (
        <OrgForecastHero forecast={data.orgForecastVsTarget} />
      )}

      {/* Row 2 — Pipeline Trend | Win Rate | Revenue Composition */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {data.pipelineTrend && data.pipelineTrend.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Pipeline Trend</SectionHeading>
            <div className="space-y-2 text-sm">
              {data.pipelineTrend.slice(-6).map((w) => (
                <div key={w.weekEnding} className="flex justify-between">
                  <span className="text-muted-foreground">{w.weekEnding}</span>
                  <span className="font-semibold">{formatCurrency(w.totalValue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.winRate && (
          <div className="flex flex-col items-center justify-center rounded-lg border bg-card p-4">
            <SectionHeading>Win Rate</SectionHeading>
            <div className="text-4xl font-bold text-primary">
              {((data.winRate.winRate ?? 0) * 100).toFixed(1)}%
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {data.winRate.wonCount ?? 0} won / {data.winRate.totalClosed ?? 0} closed
            </div>
          </div>
        )}
        {data.revenueComposition && data.revenueComposition.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Revenue Composition</SectionHeading>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="pb-1 text-left">Type</th>
                  <th className="pb-1 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.revenueComposition.map((r) => (
                  <tr key={r.revenueType} className="border-t">
                    <td className="py-1">{r.revenueType}</td>
                    <td className="py-1 text-right font-semibold">
                      {formatCurrency(r.totalValue ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Row 3 — Top 5 Deals | Team Attainment */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.topDeals && data.topDeals.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Top 5 Deals</SectionHeading>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Deal</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2">Stage</th>
                    <th className="px-3 py-2">Rep</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topDeals.slice(0, 5).map((d) => (
                    <tr key={d.opportunityId} className="border-t">
                      <td className="px-3 py-2">
                        <Link
                          to={`/opportunities/${d.opportunityId}`}
                          className="text-primary hover:underline"
                        >
                          {d.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {formatCurrency(d.estimatedValue ?? 0)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{d.stageName ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{d.assignedRepEmail ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.teamAttainment && data.teamAttainment.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Team Attainment</SectionHeading>
            <div className="space-y-3">
              {data.teamAttainment.map((row) => {
                const pct = row.attainmentPercent ?? 0;
                return (
                  <div key={row.email ?? row.userId ?? `rep-${pct}`} className="text-sm">
                    <div className="mb-1 flex justify-between">
                      <span className="font-medium">{row.email ?? '—'}</span>
                      <span className={`font-semibold ${attainmentColor(pct)}`}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {formatCurrency(row.wonAmount ?? 0)} / {formatCurrency(row.targetAmount ?? 0)}
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded bg-muted">
                      <div
                        className={`h-2 rounded transition-all ${attainmentBarColor(pct)}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Row 4 — Admin-only cards */}
      {(role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN') && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {data.userAdoption && (
            <div className="rounded-lg border bg-card p-4">
              <SectionHeading>User Adoption</SectionHeading>
              <div className="flex items-baseline gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{data.userAdoption.activeUsers ?? 0}</span>
                <span className="text-sm text-muted-foreground">
                  active / {data.userAdoption.totalUsers ?? 0} total
                </span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {data.userAdoption.inactiveUsers ?? 0} inactive
              </div>
            </div>
          )}
          {data.dataQualityIssueCount != null && (
            <div className="rounded-lg border bg-card p-4">
              <SectionHeading>Data Quality Issues</SectionHeading>
              <div className="flex items-baseline gap-2">
                <AlertTriangle
                  className={`h-5 w-5 ${data.dataQualityIssueCount > 0 ? 'text-amber-500' : 'text-green-500'}`}
                />
                <span className="text-2xl font-bold">{data.dataQualityIssueCount}</span>
              </div>
              <Link
                to="/analytics/data-quality"
                className="mt-2 inline-block text-xs text-primary hover:underline"
              >
                View details
              </Link>
            </div>
          )}
          {data.agentSuccessRate && (
            <div className="rounded-lg border bg-card p-4">
              <SectionHeading>Agent Success Rate</SectionHeading>
              <div className="flex items-baseline gap-2">
                <BotIcon className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">
                  {(data.agentSuccessRate.successRatePercent ?? 0).toFixed(1)}%
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {data.agentSuccessRate.successCount ?? 0} / {data.agentSuccessRate.totalRuns ?? 0} runs
              </div>
            </div>
          )}
        </div>
      )}

      {/* Row 5 — Demo tiles (Tenant Admin cockpit) — ticket 114.7 */}
      {(role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN') &&
        (data.demosByStatus || data.customIndustriesThisQuarter != null) && (
          <DemosAdminSection
            demosByStatus={data.demosByStatus}
            customIndustriesThisQuarter={data.customIndustriesThisQuarter}
          />
        )}

      {/* Ticket 124.6c — parent-only subtree roll-up (self-hides for leaf
          tenants / non-parents). */}
      <SubtreeRollupSection />

      {/* Row 6 — Quick Navigation (collapsible) */}
      <QuickAccessTiles collapsible />
    </div>
  );
}

function OrgForecastHero({ forecast }: { forecast: OrgForecastVsTarget }) {
  const current = forecast.forecastTotal ?? 0;
  const target = forecast.targetTotal ?? 0;
  const pct = target > 0 ? (current / target) * 100 : 0;
  const barColor = attainmentBarColor(pct);
  const textColor = attainmentColor(pct);

  return (
    <div className="rounded-lg border bg-card p-6">
      <SectionHeading>Org Forecast vs Target</SectionHeading>
      <div className="flex items-baseline gap-4">
        <span className={`text-4xl font-bold ${textColor}`}>{pct.toFixed(1)}%</span>
        <span className="text-sm text-muted-foreground">
          {formatCurrency(current)} of {formatCurrency(target)}
        </span>
      </div>
      <div className="mt-3 h-4 w-full rounded bg-muted">
        <div
          className={`h-4 rounded transition-all ${barColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Story 41.3 — Manager Dashboard                                     */
/* ================================================================== */

function ManagerDashboard({ data }: { data: DashboardData }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>

      {/* Stories 56.1 + 56.5 — pipeline temperature + realistic forecast */}
      <PipelineTemperatureWidget />
      <ForecastSummaryWidget />

      {/* Stories 56.2 + 56.3 — funnel conversion + pipeline velocity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FunnelConversionWidget />
        <PipelineVelocityWidget />
      </div>

      {/* Story 56.7 — composed alerts feed */}
      <AlertsFeedWidget />

      {/* Row 1 — Team Pipeline | Stale Deals | Pending Approvals */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {data.teamPipelineByStage && data.teamPipelineByStage.length > 0 && (
          <div className="rounded-lg border bg-card p-4 md:col-span-1">
            <SectionHeading>Team Pipeline by Stage</SectionHeading>
            <PipelineStagesBars stages={data.teamPipelineByStage} />
          </div>
        )}

        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>Stale Deals</SectionHeading>
          <div className="flex items-baseline gap-2">
            <AlertTriangle
              className={`h-5 w-5 ${(data.staleCount ?? 0) > 0 ? 'text-red-500' : 'text-green-500'}`}
            />
            <span className="text-3xl font-bold">{data.staleCount ?? 0}</span>
          </div>
          {(data.staleCount ?? 0) > 0 && (
            <Link
              to="/opportunities?stale=true"
              className="mt-2 inline-block text-xs text-primary hover:underline"
            >
              View stale deals
            </Link>
          )}
        </div>

        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>Pending Approvals</SectionHeading>
          <div className="flex items-baseline gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            <span className="text-3xl font-bold">{data.pendingApprovals ?? 0}</span>
          </div>
          {(data.pendingApprovals ?? 0) > 0 && (
            <Link
              to="/proposals"
              className="mt-2 inline-block text-xs text-primary hover:underline"
            >
              Review proposals
            </Link>
          )}
        </div>
      </div>

      {/* Row 2 — Rep Attainment | Conversion Funnel */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.repAttainment && data.repAttainment.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Rep Attainment</SectionHeading>
            <div className="space-y-3">
              {[...data.repAttainment]
                .sort((a, b) => (b.attainmentPercent ?? 0) - (a.attainmentPercent ?? 0))
                .map((row) => {
                  const pct = row.attainmentPercent ?? 0;
                  return (
                    <div key={row.email ?? row.userId ?? `rep-${pct}`} className="text-sm">
                      <div className="mb-1 flex justify-between">
                        <span className="font-medium">{row.email ?? '—'}</span>
                        <span className={`font-semibold ${attainmentColor(pct)}`}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {formatCurrency(row.wonAmount ?? 0)} / {formatCurrency(row.targetAmount ?? 0)}
                        </span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded bg-muted">
                        <div
                          className={`h-2 rounded transition-all ${attainmentBarColor(pct)}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {data.conversionFunnel && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Conversion Funnel</SectionHeading>
            <div className="flex items-center justify-center gap-4 py-4">
              <div className="text-center">
                <div className="text-xs text-muted-foreground">Lead to Opp</div>
                <div className="mt-1 text-2xl font-bold text-primary">
                  {formatPercent(data.conversionFunnel.leadToOpportunity)}
                </div>
              </div>
              <ArrowRight className="h-6 w-6 text-muted-foreground" />
              <div className="text-center">
                <div className="text-xs text-muted-foreground">Opp to Won</div>
                <div className="mt-1 text-2xl font-bold text-primary">
                  {formatPercent(data.conversionFunnel.opportunityToWon)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 3 — Risk Alerts | Activity Areas */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.riskAlerts && data.riskAlerts.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Risk Alerts</SectionHeading>
            <div className="space-y-2">
              {data.riskAlerts.slice(0, 5).map((alert) => (
                <div
                  key={alert.opportunityId}
                  className="flex items-start gap-2 rounded border p-2 text-sm"
                >
                  <RiskBadge level={alert.riskLevel} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/opportunities/${alert.opportunityId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {alert.opportunityName}
                    </Link>
                    <div className="truncate text-xs text-muted-foreground">
                      {alert.reasoning}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.activityAreas && <ActivityAreasCard areas={data.activityAreas} />}
      </div>

      {/* Row 4 — ICP Pipeline Quality (story 56.6) */}
      {data.icpPipelineQuality && (
        <IcpPipelineQualityCard quality={data.icpPipelineQuality} />
      )}

      {/* Row 5 — Demo analytics (Coach cockpit) — ticket 114.7 */}
      {(data.demoConversionByRep || data.demoRepUsageZScores) && (
        <DemosCoachSection
          conversionByRep={data.demoConversionByRep}
          usageZScores={data.demoRepUsageZScores}
        />
      )}

      {/* Row 6 — Quick Navigation (collapsible) */}
      <QuickAccessTiles collapsible />
    </div>
  );
}

function PipelineStagesBars({ stages }: { stages: TeamPipelineStage[] }) {
  const maxValue = Math.max(...stages.map((s) => s.totalValue ?? 0), 1);
  const STAGE_COLORS = [
    'bg-blue-500',
    'bg-cyan-500',
    'bg-teal-500',
    'bg-green-500',
    'bg-amber-500',
    'bg-orange-500',
    'bg-red-500',
    'bg-purple-500',
  ];

  return (
    <div className="space-y-2">
      {stages.map((stage, i) => {
        const value = stage.totalValue ?? 0;
        const pct = Math.max((value / maxValue) * 100, 2);
        const label = stage.stageName ?? `Stage ${i + 1}`;
        return (
          <div key={stage.stageId ?? `stage-${i}`} className="text-sm">
            <div className="mb-1 flex justify-between text-xs">
              <span className="font-medium">{label}</span>
              <span className="text-muted-foreground">
                {stage.count} deals &middot; {formatCurrency(value)}
              </span>
            </div>
            <div className="h-3 w-full rounded bg-muted">
              <div
                className={`h-3 rounded ${STAGE_COLORS[i % STAGE_COLORS.length]}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RiskBadge({ level }: { level: string }) {
  const color = level === 'HIGH' ? 'bg-red-900/40 text-red-300' : 'bg-amber-900/40 text-amber-300';
  return (
    <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${color}`}>
      {level}
    </span>
  );
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function IcpPipelineQualityCard({ quality }: { quality: IcpPipelineQuality }) {
  const { t } = useTranslation();

  if (!quality.icpConfigured) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <SectionHeading>{t('icp.pipeline_quality')}</SectionHeading>
        <p className="text-sm text-muted-foreground">{t('icp.not_configured')}</p>
      </div>
    );
  }

  const share = quality.icpSharePercent ?? 0;
  const shareColor = share >= 60 ? 'text-green-600' : share >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="rounded-lg border bg-card p-4">
      <SectionHeading>{t('icp.pipeline_quality')}</SectionHeading>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* ICP share */}
        <div className="flex flex-col items-center justify-center rounded-lg border p-4">
          <div className={`text-3xl font-bold ${shareColor}`}>
            {Number(share).toFixed(1)}%
          </div>
          <div className="mt-1 text-center text-xs text-muted-foreground">
            {t('icp.of_pipeline')}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div className="text-muted-foreground">{t('icp.icp_pipeline')}</div>
            <div className="text-right font-semibold">{formatCurrency(Number(quality.icpPipelineValue))}</div>
            <div className="text-muted-foreground">{t('icp.total_pipeline')}</div>
            <div className="text-right font-semibold">{formatCurrency(Number(quality.totalPipelineValue))}</div>
          </div>
        </div>

        {/* AI close probability */}
        <div className="flex flex-col items-center justify-center rounded-lg border p-4">
          <div className="text-xs font-medium text-muted-foreground">{t('icp.avg_ai_close_prob')}</div>
          {quality.avgAiCloseProb != null ? (
            <div className="mt-1 text-3xl font-bold text-primary">
              {(Number(quality.avgAiCloseProb) * 100).toFixed(1)}%
            </div>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">—</div>
          )}
        </div>

        {/* Out-of-ICP alerts */}
        <div className="rounded-lg border p-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('icp.out_of_icp_alerts')}
          </div>
          {quality.outOfIcpAlerts.length === 0 ? (
            <div className="text-sm text-muted-foreground">—</div>
          ) : (
            <div className="space-y-1">
              {quality.outOfIcpAlerts.slice(0, 5).map((alert) => (
                <div key={alert.opportunityId} className="flex items-center justify-between text-xs">
                  <span className="truncate font-medium">{alert.opportunityName}</span>
                  <span className="ml-2 shrink-0 text-muted-foreground">
                    {formatCurrency(Number(alert.estimatedValue))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityAreasCard({ areas }: { areas: ActivityAreasData }) {
  const cards: { metric: AreaMetric; icon: LucideIcon }[] = [
    { metric: areas.demandGeneration, icon: Megaphone },
    { metric: areas.maturation, icon: Activity },
    { metric: areas.close, icon: Briefcase },
    { metric: areas.delivery, icon: Target },
  ];

  return (
    <div className="rounded-lg border bg-card p-4">
      <SectionHeading>Activity Areas</SectionHeading>
      <div className="grid grid-cols-2 gap-3">
        {cards.map(({ metric, icon: Icon }) => (
          <div key={metric.label} className="flex items-center gap-3 rounded-lg border p-3">
            <Icon className="h-5 w-5 text-primary" />
            <div>
              <div className="text-lg font-bold">{metric.count}</div>
              <div className="text-xs text-muted-foreground">{metric.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Story 41.4 — Finance Dashboard                                     */
/* ================================================================== */

function FinanceDashboard({ data }: { data: DashboardData }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>

      {/* Row 1 — Revenue Closed (hero) */}
      {data.revenueClosed && (
        <div className="rounded-lg border bg-card p-6">
          <SectionHeading>Revenue Closed</SectionHeading>
          <div className="text-4xl font-bold text-primary">
            {formatCurrency(data.revenueClosed.total)}
          </div>
        </div>
      )}

      {/* Row 2 — Pipeline Weighted | Margin Summary | Forecast vs Actual */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {data.pipelineWeighted && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Pipeline Weighted</SectionHeading>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Raw Pipeline</span>
                <span className="font-semibold">
                  {formatCurrency(data.pipelineWeighted.rawPipeline ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Weighted</span>
                <span className="font-semibold">
                  {formatCurrency(data.pipelineWeighted.weightedValue ?? 0)}
                </span>
              </div>
            </div>
          </div>
        )}

        {data.marginSummary && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Margin Summary</SectionHeading>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue</span>
                <span className="font-semibold">
                  {formatCurrency(data.marginSummary.totalRevenue ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cost</span>
                <span className="font-semibold">
                  {formatCurrency(data.marginSummary.totalCost ?? 0)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground">Margin</span>
                <span
                  className={`font-semibold ${(data.marginSummary.totalMargin ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {formatCurrency(data.marginSummary.totalMargin ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin %</span>
                <span
                  className={`font-semibold ${(data.marginSummary.averageMarginPercent ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {(data.marginSummary.averageMarginPercent ?? 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {data.forecastVsActual && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Forecast vs Actual</SectionHeading>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Forecast</span>
                <span className="font-semibold">
                  {formatCurrency(data.forecastVsActual.forecast ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actual</span>
                <span className="font-semibold">
                  {formatCurrency(data.forecastVsActual.actual ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-muted-foreground">Variance</span>
                <span className="flex items-center gap-1 font-semibold">
                  {(data.forecastVsActual.variance ?? 0) >= 0 ? (
                    <ArrowUp className="h-3 w-3 text-green-500" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-red-500" />
                  )}
                  {formatCurrency(Math.abs(data.forecastVsActual.variance ?? 0))}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 3 — Revenue by Type | Recurring vs One-Time */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.revenueClosed?.byType && data.revenueClosed.byType.length > 0 && (
          <div className="rounded-lg border bg-card p-4">
            <SectionHeading>Revenue by Type</SectionHeading>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.revenueClosed.byType.map((r) => (
                    <tr key={r.type} className="border-t">
                      <td className="px-3 py-2">{r.type}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {formatCurrency(r.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.recurringVsOneTime && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col items-center justify-center rounded-lg border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground">Recurring</div>
              <div className="mt-1 text-2xl font-bold text-primary">
                {formatCurrency(data.recurringVsOneTime.recurringRevenue ?? 0)}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground">One-Time</div>
              <div className="mt-1 text-2xl font-bold text-primary">
                {formatCurrency(data.recurringVsOneTime.oneTimeRevenue ?? 0)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Row 4 — Commission Accruals */}
      {data.commissionAccruals?.byRep && data.commissionAccruals.byRep.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>Commission Accruals</SectionHeading>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Rep</th>
                  <th className="px-3 py-2 text-right">Accrued</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.commissionAccruals.byRep.map((r) => (
                  <tr key={r.rep} className="border-t">
                    <td className="px-3 py-2 font-medium">{r.rep}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(r.accrued)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(r.paid)}</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {formatCurrency(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Row 5 — Quick Navigation (collapsible) */}
      <QuickAccessTiles collapsible />
    </div>
  );
}

/* ================================================================== */
/*  Story 41.5 — Rep Dashboard (default / fallback)                    */
/* ================================================================== */

function RepDashboard({ data }: { data: DashboardData }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('nav.dashboard')}</h1>

      {/* Story 121.8 — vertical-driven tiles (self-hide if none). */}
      <VerticalTileGrid />

      {/* Row 1 — Quota Attainment + Forecast */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RepQuotaCard attainment={data.quotaAttainment} noQuota={data.noQuota ?? false} />
        {data.forecastBuckets && data.forecastBuckets.length > 0 && (
          <RepForecastCard buckets={data.forecastBuckets} />
        )}
      </div>

      {/* Row 2 — Open Opps + Quick Access (not collapsed for reps) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {user && (
          <InfoCard
            label="Open Opportunities"
            value={String(data.openOpportunityCount ?? 0)}
          />
        )}
      </div>
      <QuickAccessTiles collapsible={false} />

      {/* Row 3 — Agent Insights */}
      {data.agentLog && data.agentLog.length > 0 && (
        <AgentInsightsSummary entries={data.agentLog} />
      )}

      {/* Row 4 — Demo tiles (Closer cockpit) — ticket 114.7 */}
      {(data.demosAwaitingSend || data.recentDemoCtaClicks != null) && (
        <DemosCloserSection
          demosAwaitingSend={data.demosAwaitingSend}
          recentCtaClicks={data.recentDemoCtaClicks}
        />
      )}

      {/* Row 5 — Demo tiles (Pre-Sales cockpit) — ticket 114.7 */}
      {data.demosAwaitingRefinement != null && (
        <DemosPreSalesSection demosAwaitingRefinement={data.demosAwaitingRefinement} />
      )}
    </div>
  );
}

function RepQuotaCard({
  attainment,
  noQuota,
}: {
  attainment?: QuotaAttainment | null;
  noQuota: boolean;
}) {
  if (noQuota && !attainment) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <SectionHeading>My Quota Attainment</SectionHeading>
        <div className="text-sm text-muted-foreground">No quota assigned for current period</div>
      </div>
    );
  }

  if (!attainment) return null;

  const pct = Number(attainment.attainmentPercent);
  const barWidth = Math.min(pct, 100);
  const barColor = pct >= 100 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="rounded-lg border bg-card p-4">
      <SectionHeading>My Quota Attainment</SectionHeading>
      <div className="mb-3 grid grid-cols-3 gap-3 text-center text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Target</div>
          <div className="font-semibold">
            {attainment.currency} {Number(attainment.targetAmount).toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Closed</div>
          <div className="font-semibold">
            {attainment.currency} {Number(attainment.wonAmount).toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Attainment</div>
          <div className="font-semibold">{pct.toFixed(1)}%</div>
        </div>
      </div>
      <div className="h-4 w-full rounded bg-muted">
        <div
          className={`h-4 rounded ${barColor} transition-all`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        {attainment.periodStart} to {attainment.periodEnd}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Ticket 114.7 — Demo dashboard tile sub-components                  */
/* ================================================================== */

function DemosPreSalesSection({
  demosAwaitingRefinement,
}: {
  demosAwaitingRefinement: DemoCockpitRow[];
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border bg-card p-4">
      <SectionHeading>{t('dashboard.demos.preSales.awaitingRefinement.title')}</SectionHeading>
      {demosAwaitingRefinement.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('dashboard.demos.preSales.awaitingRefinement.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {demosAwaitingRefinement.slice(0, 5).map((demo) => (
            <div key={demo.id} className="flex items-center justify-between rounded border p-2 text-sm">
              <span className="font-medium">{demo.businessName}</span>
              <span className="text-xs text-muted-foreground">{demo.industryKey}</span>
            </div>
          ))}
        </div>
      )}
      <Link to="/demos" className="mt-2 inline-block text-xs text-primary hover:underline">
        {t('dashboard.demos.preSales.awaitingRefinement.viewAll')}
      </Link>
    </div>
  );
}

function DemosCloserSection({
  demosAwaitingSend,
  recentCtaClicks,
}: {
  demosAwaitingSend?: DemoCockpitRow[] | null;
  recentCtaClicks?: number | null;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {demosAwaitingSend != null && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.closer.awaitingSend.title')}</SectionHeading>
          {demosAwaitingSend.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('dashboard.demos.closer.awaitingSend.empty')}
            </p>
          ) : (
            <div className="space-y-2">
              {demosAwaitingSend.slice(0, 5).map((demo) => (
                <div key={demo.id} className="flex items-center justify-between rounded border p-2 text-sm">
                  <span className="font-medium">{demo.businessName}</span>
                  <span className="text-xs text-muted-foreground">{demo.industryKey}</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/demos" className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('dashboard.demos.closer.awaitingSend.viewAll')}
          </Link>
        </div>
      )}

      {recentCtaClicks != null && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.closer.recentCtaClicks.title')}</SectionHeading>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-primary">{recentCtaClicks}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('dashboard.demos.closer.recentCtaClicks.empty')}
          </p>
          <Link to="/demos" className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('dashboard.demos.closer.recentCtaClicks.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}

function DemosCoachSection({
  conversionByRep,
  usageZScores,
}: {
  conversionByRep?: DemoConversionByRep[] | null;
  usageZScores?: DemoRepUsageZScore[] | null;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {conversionByRep != null && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.coach.conversionByRep.title')}</SectionHeading>
          {conversionByRep.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('dashboard.demos.coach.conversionByRep.empty')}
            </p>
          ) : (
            <div className="space-y-2">
              {conversionByRep.slice(0, 5).map((row) => (
                <div key={row.repId} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{row.repEmail ?? row.repId.slice(0, 8)}</span>
                  <span className="text-muted-foreground">
                    {row.demosSent} sent · {Number(row.conversionRate).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
          <Link to="/demos/analytics" className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('dashboard.demos.coach.conversionByRep.viewAll')}
          </Link>
        </div>
      )}

      {usageZScores != null && usageZScores.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.coach.underOverUse.title')}</SectionHeading>
          <div className="space-y-2">
            {usageZScores
              .filter((z) => z.usageFlag !== 'NORMAL')
              .slice(0, 5)
              .map((z) => (
                <div key={z.repId} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{z.repEmail ?? z.repId.slice(0, 8)}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      z.usageFlag === 'OVER'
                        ? 'bg-emerald-900/40 text-emerald-200'
                        : 'bg-amber-900/40 text-amber-200'
                    }`}
                  >
                    {z.usageFlag}
                  </span>
                </div>
              ))}
            {usageZScores.every((z) => z.usageFlag === 'NORMAL') && (
              <p className="text-sm text-muted-foreground">
                {t('dashboard.demos.coach.underOverUse.empty')}
              </p>
            )}
          </div>
          <Link to="/demos/analytics" className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('dashboard.demos.coach.underOverUse.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}

function DemosAdminSection({
  demosByStatus,
  customIndustriesThisQuarter,
}: {
  demosByStatus?: DemoStatusCount[] | null;
  customIndustriesThisQuarter?: number | null;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {demosByStatus != null && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.admin.byStatus.title')}</SectionHeading>
          {demosByStatus.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('dashboard.demos.admin.byStatus.empty')}
            </p>
          ) : (
            <div className="space-y-1">
              {demosByStatus.map((row) => (
                <div key={row.status} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{row.status}</span>
                  <span className="text-muted-foreground">{row.count}</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/demos" className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('dashboard.demos.admin.byStatus.viewAll')}
          </Link>
        </div>
      )}

      {customIndustriesThisQuarter != null && (
        <div className="rounded-lg border bg-card p-4">
          <SectionHeading>{t('dashboard.demos.admin.customIndustriesThisQuarter.title')}</SectionHeading>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-primary">{customIndustriesThisQuarter}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {customIndustriesThisQuarter === 0
              ? t('dashboard.demos.admin.customIndustriesThisQuarter.empty')
              : t('dashboard.demos.admin.customIndustriesThisQuarter.viewAll')}
          </p>
        </div>
      )}
    </div>
  );
}

const FORECAST_ORDER = ['COMMIT', 'BEST_CASE', 'UPSIDE', 'PIPELINE'];

function RepForecastCard({ buckets }: { buckets: ForecastBucket[] }) {
  const sorted = [...buckets].sort(
    (a, b) =>
      FORECAST_ORDER.indexOf(a.classification) - FORECAST_ORDER.indexOf(b.classification),
  );
  const grandTotal = sorted.reduce((s, b) => s + Number(b.totalValue), 0);

  return (
    <div className="rounded-lg border bg-card p-4">
      <SectionHeading>Forecast Snapshot</SectionHeading>
      <div className="grid grid-cols-2 gap-3">
        {sorted.map((bucket) => (
          <div key={bucket.classification} className="rounded-lg border p-3 text-center">
            <div className="text-xs font-medium text-muted-foreground">
              {bucket.classification.replace('_', ' ')}
            </div>
            <div className="mt-1 text-lg font-bold">
              {formatCurrency(Number(bucket.totalValue))}
            </div>
            <div className="text-xs text-muted-foreground">{bucket.count ?? 0} deals</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-right text-sm text-muted-foreground">
        Total: <span className="font-semibold">{formatCurrency(grandTotal)}</span>
      </div>
    </div>
  );
}
